import { Course } from '@yandex-int/k-common/typings';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Action, bindActionCreators, compose, Dispatch } from 'redux';

import { LMS_HOMEWORK_ID_KEY } from 'common.components/constants';
import { getSavedOrFirstTeamId, getCurrentClass, getIsClassInAvailableClasses } from 'common.components/user/selectors';
import * as courseTemplateActions from 'store/courses-templates/actions';
import { actions as coursesActions } from 'store/courses/actions';
import { getShouldLoadCourses, getSubjects } from 'store/courses/selectors';

import type { ReduxTeacherStore } from '../../typings';
import { getIsCalendarPlanOpened } from '../library.calendar-plan-showcase/selectors';
import * as libraryActions from '../library.store/actions';

import { LibraryView } from './library.view';

import './library.scss';

type StoreProps = ReturnType<ReturnType<typeof buildMapStateToProps>>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface HOCProps extends WithRouterPathProps, WrappedComponentProps {}

interface Props extends StoreProps, DispatchProps, HOCProps {}

interface Context {
    data: {
        getCourses?: Array<Course>;
    };
    initialRender: boolean;
}

export class LibraryBase extends React.Component<Props> {
    static contextTypes = {
        data: PropTypes.object,
        initialRender: PropTypes.bool.isRequired,
        staticUrls: PropTypes.object.isRequired,
    };

    constructor(props: Props, context: Context) {
        super(props, context);

        const { getCoursesByTeamRequest, getCoursesByTeamSuccess, shouldLoadCourses } = props;
        const { initialRender, data } = context;
        const classId = this.getClassIdFromParams();

        if (classId) {
            if (initialRender && data.getCourses) {
                getCoursesByTeamSuccess({ teamId: classId, courses: data.getCourses });
            } else if (shouldLoadCourses) {
                getCoursesByTeamRequest({ teamId: classId });
            }
        }
    }

    componentDidMount() {
        const { isPreview, getCoursesPreviewRequest } = this.props;

        if (isPreview) {
            getCoursesPreviewRequest();
        }
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps: Props) {
        const {
            getCoursesByTeamRequest,
            history,
            getRouterPath,
            subjects,
            location,
            match: {
                params: { subject },
            },
            shouldLoadCourses,
        } = this.props;
        const { subjects: prevSubjects, location: prevLocation } = prevProps;
        const {
            staticUrls: { labLibrarySubject },
        } = this.context;
        const classId = this.getClassIdFromParams();

        if (prevSubjects.length === 0 && subjects.length !== 0 && !subject) {
            const newSubject = subjects[0]?.subject;
            history.replace(getRouterPath(labLibrarySubject, { subject: newSubject }) + location.search);
        }

        if (classId && shouldLoadCourses) {
            getCoursesByTeamRequest({ teamId: classId });
        }

        if (prevLocation !== location) {
            const { search: prevSearch } = prevLocation;
            const { search } = location;
            const { [LMS_HOMEWORK_ID_KEY]: prevLmsHomeworkSearchParam } = qs.parse(prevSearch);
            const { [LMS_HOMEWORK_ID_KEY]: lmsHomeworkSearchParam, ...restParams } = qs.parse(search);

            if (search !== prevSearch && prevLmsHomeworkSearchParam && !lmsHomeworkSearchParam) {
                history.replace({
                    search: qs.stringify({ ...restParams, [LMS_HOMEWORK_ID_KEY]: prevLmsHomeworkSearchParam }),
                });
            }
        }
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    private getClassIdFromParams(): number | null {
        const {
            match: { params },
        } = this.props;

        const classId = Number(params.classId);

        if (isNaN(classId)) {
            return null;
        }

        return classId;
    }

    render() {
        const { savedOrFirstTeamId, canShowClass, isCalendarPlanOpened } = this.props;

        return (
            <LibraryView
                canShowClass={canShowClass}
                isCalendarPlanOpened={isCalendarPlanOpened}
                isPreview={this.props.isPreview}
                savedOrFirstTeamId={savedOrFirstTeamId}
            />
        );
    }
}

function buildMapStateToProps(isPreview?: boolean) {
    return (storeState: ReduxTeacherStore, ownProps: HOCProps) => {
        const {
            location: { search },
            match: {
                params: { classId },
            },
        } = ownProps;
        const query = qs.parse(search);
        const currentTeam = getCurrentClass(storeState, { classId });
        const grade = currentTeam?.level || Number(query.grade);

        return {
            isPreview,
            savedOrFirstTeamId: getSavedOrFirstTeamId(storeState),
            canShowClass: getIsClassInAvailableClasses(storeState, { classId }),
            subjects: isPreview ? [] : getSubjects(storeState, { classId }),
            shouldLoadCourses: getShouldLoadCourses(storeState, { classId }),
            isCalendarPlanOpened: getIsCalendarPlanOpened(storeState),
            grade,
        };
    };
}

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    return bindActionCreators(
        {
            getCoursesByTeamRequest: coursesActions.getCoursesByTeamRequest,
            getCoursesByTeamSuccess: coursesActions.getCoursesByTeamSuccess,
            getCoursesTemplatesRequest: courseTemplateActions.getCoursesTemplatesRequest,
            getCoursesPreviewRequest: libraryActions.getCoursesPreviewRequest,
        },
        dispatch
    );
};

export const Library = compose(
    withRouterPath,
    injectIntl,
    connect(buildMapStateToProps(false), mapDispatchToProps)
)(LibraryBase);

export const LibraryPreview = compose(
    withRouterPath,
    injectIntl,
    connect(buildMapStateToProps(true), mapDispatchToProps)
)(LibraryBase);
